import { useCallback, useMemo } from 'react';
import {
  watchOpsBoxesHere,
  watchOpsBranches,
  watchOpsCarrierItems,
  watchOpsExportContainers,
  watchOpsImportContainers,
  watchOpsInboundTrips,
  watchOpsLoadingTrips,
  watchOpsUnpaidShipments,
  watchOpsVehicles,
  type BoxRow,
  type CarrierKind,
  type OpsBranch,
  type OpsCarrierItem,
  type OpsContainer,
  type OpsTrip,
  type OpsVehicle,
  type ShipmentRow,
} from '@ecobox/firebase';
import { useServices } from '../services';
import { useWatch, type Watched } from './useWatch';

const NONE: never[] = [];

/** Trips leaving my branch that are still being loaded. */
export function useLoadingTrips(branchId: string | null): Watched<OpsTrip[]> {
  const { db } = useServices().client;
  const start = useCallback(
    (d: (v: OpsTrip[]) => void, e: (err: Error) => void) =>
      watchOpsLoadingTrips(db, branchId ?? '', d, e),
    [db, branchId],
  );
  return useWatch(branchId ? start : null, NONE);
}

/** Trips on their way to my branch, or being received. */
export function useInboundTrips(branchId: string | null): Watched<OpsTrip[]> {
  const { db } = useServices().client;
  const start = useCallback(
    (d: (v: OpsTrip[]) => void, e: (err: Error) => void) =>
      watchOpsInboundTrips(db, branchId ?? '', d, e),
    [db, branchId],
  );
  return useWatch(branchId ? start : null, NONE);
}

/** Containers on the export side of my branch. */
export function useExportContainers(branchId: string | null): Watched<OpsContainer[]> {
  const { db } = useServices().client;
  const start = useCallback(
    (d: (v: OpsContainer[]) => void, e: (err: Error) => void) =>
      watchOpsExportContainers(db, branchId ?? '', d, e),
    [db, branchId],
  );
  return useWatch(branchId ? start : null, NONE);
}

/** Containers on the import side of my branch. */
export function useImportContainers(branchId: string | null): Watched<OpsContainer[]> {
  const { db } = useServices().client;
  const start = useCallback(
    (d: (v: OpsContainer[]) => void, e: (err: Error) => void) =>
      watchOpsImportContainers(db, branchId ?? '', d, e),
    [db, branchId],
  );
  return useWatch(branchId ? start : null, NONE);
}

export function useBranches(): Watched<OpsBranch[]> {
  const { db } = useServices().client;
  const start = useCallback(
    (d: (v: OpsBranch[]) => void, e: (err: Error) => void) => watchOpsBranches(db, d, e),
    [db],
  );
  return useWatch(start, NONE);
}

/** What is expected on a trip or in a container, and what has been scanned off. */
export function useCarrierItems(kind: CarrierKind, id: string | null): Watched<OpsCarrierItem[]> {
  const { db } = useServices().client;
  const start = useCallback(
    (d: (v: OpsCarrierItem[]) => void, e: (err: Error) => void) =>
      watchOpsCarrierItems(db, { kind, id: id ?? '' }, d, e),
    [db, kind, id],
  );
  return useWatch(id ? start : null, NONE);
}

export interface BranchCargo {
  boxes: ReadonlyMap<string, BoxRow>;
  unpaid: ReadonlyMap<string, ShipmentRow>;
  ready: boolean;
}

/**
 * The boxes at my branch and their unpaid shipments, kept in memory (and in Firestore's own
 * offline cache) so a box can be checked the moment it is scanned.
 */
export function useBranchCargo(branchId: string | null, enabled: boolean): BranchCargo {
  const { db } = useServices().client;
  const startBoxes = useCallback(
    (d: (v: BoxRow[]) => void, e: (err: Error) => void) =>
      watchOpsBoxesHere(db, branchId ?? '', d, e),
    [db, branchId],
  );
  const startUnpaid = useCallback(
    (d: (v: ShipmentRow[]) => void, e: (err: Error) => void) =>
      watchOpsUnpaidShipments(db, branchId ?? '', d, e),
    [db, branchId],
  );
  const live = enabled && branchId !== null;
  const boxes = useWatch(live ? startBoxes : null, NONE as BoxRow[]);
  const unpaid = useWatch(live ? startUnpaid : null, NONE as ShipmentRow[]);
  const boxMap = useMemo(() => new Map(boxes.data.map((b) => [b.barcode, b])), [boxes.data]);
  const unpaidMap = useMemo(
    () => new Map(unpaid.data.map((s) => [s.trackingNo, s])),
    [unpaid.data],
  );
  return { boxes: boxMap, unpaid: unpaidMap, ready: !boxes.loading && !unpaid.loading };
}

/** The vehicles a trip from my branch may use. */
export function useVehicles(branchId: string | null): Watched<OpsVehicle[]> {
  const { db } = useServices().client;
  const start = useCallback(
    (d: (v: OpsVehicle[]) => void, e: (err: Error) => void) =>
      watchOpsVehicles(db, branchId ?? '', d, e),
    [db, branchId],
  );
  return useWatch(branchId ? start : null, NONE);
}
